import RenderContentStatus from "@src/components/RenderContentStatus";
import Spinner from "@src/components/Spinner";
import TableComponent from "@src/components/TableComponent";
import { useFetchUntaggedTracksQuery } from "@src/queries/trackQueries";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import {
  setSelectedTrack,
  setTracksCount,
} from "@src/redux/slices/tagFactorySlice";
import { ApiResponse } from "@src/types/httpApi";
import { ITrack } from "@src/types/track";
import { AxiosResponse } from "axios";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import Tooltip from "@src/components/Tooltip";
import { TagIcon } from "lucide-react";
import { theme } from "@src/theme";
import styled from "styled-components";
import TrackTagsModal from "@src/components/TrackTagsModal/TrackTagsModal";
import useModal from "@src/hooks/useModal";
import { useRole } from "@src/hooks/useAuth";

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  max-width: 200px;
`;

const TagPill = styled.span`
  background-color: ${theme.colors.yellow};
  color: #000;
  border: 1px solid ${theme.colors.yellow};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
`;

const TagsIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

type UntaggedTracksTable = {};
const UntaggedTracksTable: React.FC<UntaggedTracksTable> = () => {
  const {
    isOpen: isTagsModalOpen,
    openModal: openTagsModal,
    closeModal: closeTagsModal,
  } = useModal();
  const [selectedTrackForTags, setSelectedTrackForTags] =
    React.useState<ITrack | null>(null);
  const role = useRole();
  const canEditTags = role === "admin" || role === "manager";

  const TagsWithTooltip = ({ row }: { row: ITrack }): React.JSX.Element => {
    const emotionArray =
      row.emotion && !Array.isArray(row.emotion)
        ? [row.emotion]
        : Array.isArray(row.emotion)
          ? row.emotion
          : [];
    const genreArray =
      row.genre && !Array.isArray(row.genre)
        ? [row.genre]
        : Array.isArray(row.genre)
          ? row.genre
          : [];

    const allTags = [...(row.tags || []), ...emotionArray, ...genreArray];

    const formatTagsForTooltip = () => {
      const sections = [];
      if (row.tags && Array.isArray(row.tags) && row.tags.length > 0) {
        sections.push(`Tags: ${row.tags.map((t) => t.title).join(", ")}`);
      }
      const emotion =
        row.emotion && !Array.isArray(row.emotion)
          ? row.emotion
          : Array.isArray(row.emotion) && row.emotion.length > 0
            ? row.emotion[0]
            : null;
      if (emotion && emotion.title) {
        sections.push(`Emotion: ${emotion.title}`);
      }
      const genre =
        row.genre && !Array.isArray(row.genre)
          ? row.genre
          : Array.isArray(row.genre) && row.genre.length > 0
            ? row.genre[0]
            : null;
      if (genre && genre.title) {
        sections.push(`Genre: ${genre.title}`);
      }
      return sections.length > 0 ? sections.join("\n") : "No tags";
    };

    const tooltipContent = formatTagsForTooltip();

    if (!canEditTags) {
      return (
        <TagsContainer>
          {allTags.slice(0, 3).map((tag) => (
            <TagPill key={tag.documentId} title={tag.title}>
              {tag.title}
            </TagPill>
          ))}
          {allTags.length > 3 && <TagPill>+{allTags.length - 3}</TagPill>}
          {allTags.length === 0 && (
            <span
              style={{
                color: theme.colors.gray,
                fontStyle: "italic",
                fontSize: "11px",
              }}
            >
              -
            </span>
          )}
        </TagsContainer>
      );
    }

    return (
      <Tooltip
        content={tooltipContent}
        contentStyle={{
          whiteSpace: "pre-line",
          maxWidth: "300px",
        }}
      >
        <TagsIconContainer
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTrackForTags(row);
            openTagsModal();
          }}
        >
          <TagIcon size={18} style={{ color: theme.colors.yellow }} />
          <TagsContainer>
            {allTags.slice(0, 3).map((tag) => (
              <TagPill key={tag.documentId} title={tag.title}>
                {tag.title}
              </TagPill>
            ))}
            {allTags.length > 3 && <TagPill>+{allTags.length - 3}</TagPill>}
            {allTags.length === 0 && (
              <span
                style={{
                  color: theme.colors.gray,
                  fontStyle: "italic",
                  fontSize: "11px",
                }}
              >
                -
              </span>
            )}
          </TagsContainer>
        </TagsIconContainer>
      </Tooltip>
    );
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "#",
        accessor: "display_row_index",
        width: "30px",
      },
      {
        Header: "ID",
        accessor: "id",
        width: "30px",
      },
      {
        Header: "Title",
        accessor: "title",
      },
      {
        Header: "Artist",
        accessor: "artist",
        enableSort: true,
      },
      {
        Header: "Album",
        accessor: "album",
        enableSort: true,
      },
      {
        Header: "Label",
        accessor: "label",
        enableSort: true,
      },
      {
        Header: "Tags",
        accessor: "tags",
        Cell: ({ row }: { row: ITrack }): React.JSX.Element => (
          <TagsWithTooltip row={row} />
        ),
        width: "200px",
        minWidth: "150px",
      },
    ];
  }, []);
  const { filters } = useAppSelector((state) => state.tagFactory);

  const filterIds = useMemo(
    () => ({
      genre: filters.genre?.map((tag) => tag.documentId) || [],
      emotion: filters.emotion?.map((tag) => tag.documentId) || [],
      tags: filters.tags?.map((tag) => tag.documentId) || [],
    }),
    [filters]
  );

  const {
    data,
    isLoading: untaggedTracksLoading,
    fetchNextPage: fetchNextUntaggedTracksPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchUntaggedTracksQuery(filterIds);

  const dispatch = useAppDispatch();
  const totalCount = useMemo(
    () =>
      (data?.pages?.[0] as AxiosResponse<ApiResponse<ITrack[]>>)?.data?.meta
        ?.pagination?.total ?? "0",
    [data]
  );
  const tracks = useMemo(
    () =>
      ((data?.pages as AxiosResponse<ApiResponse<ITrack[]>>[]) ?? []).flatMap(
        (response) => response.data.data
      ) || [],
    [data]
  );

  useEffect(() => {
    if (selectedTrackForTags?.documentId && tracks.length > 0) {
      const updatedTrack = tracks.find(
        (t) => t.documentId === selectedTrackForTags.documentId
      );
      if (updatedTrack) {
        setSelectedTrackForTags(updatedTrack);
      }
    }
  }, [tracks, selectedTrackForTags?.documentId]);

  useEffect(() => {
    dispatch(setTracksCount(totalCount));
  }, [dispatch, totalCount]);

  const observerRef = useRef<HTMLTableRowElement | null>(null);
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextUntaggedTracksPage();
      }
    },
    [hasNextPage, isFetchingNextPage, hasNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    if (observerRef.current) observer.observe(observerRef.current);

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [handleObserver]);

  const onRowClick = (row: ITrack) => {
    dispatch(setSelectedTrack(row));
  };
  useEffect(() => {
    return () => {
      dispatch(setSelectedTrack(null));
    };
  }, []);
  return (
    <>
      <TrackTagsModal
        isOpen={isTagsModalOpen}
        onClose={() => {
          closeTagsModal();
          setSelectedTrackForTags(null);
        }}
        track={selectedTrackForTags}
        onTrackUpdate={(updatedTrack) => {
          setSelectedTrackForTags(updatedTrack);
        }}
        source="untagged"
      />
      <div
        style={{
          display: "inline-block",
          overflow: "auto",
          width: "100%",
        }}
      >
        <TableComponent
          columns={columns}
          data={tracks}
          onRowClick={onRowClick}
          loaderRef={observerRef}
        />
        <RenderContentStatus
          loading={untaggedTracksLoading}
          noResultsFoundMessage="No untagged tracks found"
          results={totalCount}
        />
        {isFetchingNextPage && <Spinner />}
      </div>
    </>
  );
};

export default UntaggedTracksTable;
